/**
 * lib/LegalContext.tsx
 * ---------------------
 * What: React context that manages legal documents, acceptances, and related
 * flows (e.g., prompting for acceptance).
 * Why: Central location to ensure legal compliance UX is consistent across the
 * app and easy to evolve.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
// import { LegalAcceptanceModal } from './LegalAcceptanceModal';
import LegalAcceptanceModal from '../app/components/LegalAcceptanceModal';

export type LegalDocType = 'terms' | 'privacy' | 'gdpr_special';

interface LegalDocument {
  id: string;
  doc_type: LegalDocType;
  version: string;
  url: string;
  summary_changes: string | null;
}

interface AcceptedDocument {
  id: string;
  doc_type: LegalDocType;
  version: string;
  url: string;
  accepted_at: string;
}

interface LegalContextValue {
  loading: boolean;
  pendingDocuments: LegalDocument[];
  acceptedDocuments: AcceptedDocument[];
  refresh: () => Promise<void>;
  acceptDocuments: (docIds: string[]) => Promise<void>;
  showModal: boolean;
  dismissModal: () => void;
}

const LegalContext = createContext<LegalContextValue | undefined>(undefined);

export function LegalProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingDocuments, setPendingDocuments] = useState<LegalDocument[]>([]);
  const [acceptedDocuments, setAcceptedDocuments] = useState<AcceptedDocument[]>([]);
  const userIdRef = React.useRef<string | null>(null);
  // Track which user we have fetched for in this app session
  const fetchedForUserIdRef = React.useRef<string | null>(null);
  const shownThisSessionRef = React.useRef<boolean>(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  const fetchData = useCallback(async () => {
    if (!user) {
      setPendingDocuments([]);
      setAcceptedDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const runForUser = user.id; // capture
    try {
      // Fetch every document that is marked as current
      const { data: docs, error: docsError } = await supabase
        .from('legal_documents')
        .select('id, doc_type, version, url, summary_changes')
        .eq('is_current', true);

      if (docsError) throw docsError;

      // Fetch all acceptances for this user
      const { data: accepts, error: accErr } = await supabase
        .from('document_acceptances')
        .select('document_id, accepted_at')
        .eq('user_id', user.id);

      if (accErr) throw accErr;

      const acceptedIds = new Set((accepts || []).map((r: { document_id: string }) => r.document_id));

      const pending = (docs || []).filter((d: any) => !acceptedIds.has(d.id));
      // If auth state changed while we were fetching, ignore results
      if (userIdRef.current !== runForUser) {
        return;
      }
      setPendingDocuments(pending as LegalDocument[]);

      const acceptedList: AcceptedDocument[] = [];
      if ((accepts || []).length) {
        const idArray = (accepts || []).map((r: any) => r.document_id);
        const { data: docsForAccepted, error: docsAccErr } = await supabase
          .from('legal_documents')
          .select('id, doc_type, version, url')
          .in('id', idArray);
        if (docsAccErr) throw docsAccErr;
        const docMap = new Map((docsForAccepted || []).map((d: any) => [d.id, d]));
        (accepts || []).forEach((a: any) => {
          const d = docMap.get(a.document_id);
          if (d) {
            acceptedList.push({
              id: a.document_id,
              doc_type: d.doc_type,
              version: d.version,
              url: d.url,
              accepted_at: a.accepted_at,
            });
          }
        });
      }
      // If auth state changed while we were fetching, ignore results
      if (userIdRef.current !== runForUser) {
        return;
      }
      setAcceptedDocuments(acceptedList);

      // If there are pending documents and we haven't prompted in this session, show once
      if ((pending as LegalDocument[]).length > 0 && !shownThisSessionRef.current) {
        shownThisSessionRef.current = true;
        setShowModal(true);
      }
      if ((pending as LegalDocument[]).length === 0) {
        setShowModal(false);
      }

      // If the user previously ticked acceptance during sign-up (before account verification),
      // auto-accept any still-pending current documents.
      try {
        const preAccepted = await AsyncStorage.getItem('preAcceptedLegalDocs');
        if (preAccepted === 'true' && pending.length) {
          const ids = pending.map((d: any) => d.id);
          await acceptDocuments(ids);
          await AsyncStorage.removeItem('preAcceptedLegalDocs');
          return; // acceptDocuments will trigger refreshData again
        }
      } catch {}
    } catch (err) {
      console.error('[LegalProvider] Error fetching legal status', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch ONLY once per login. On logout, clear state without refetching.
  useEffect(() => {
    // Logged out → clear state and reset session flags
    if (!user) {
      setPendingDocuments([]);
      setAcceptedDocuments([]);
      setShowModal(false);
      shownThisSessionRef.current = false;
      fetchedForUserIdRef.current = null;
      setLoading(false);
      return;
    }
    // Logged in → fetch only if not already fetched for this user during this app session
    if (fetchedForUserIdRef.current !== user.id) {
      fetchedForUserIdRef.current = user.id;
      shownThisSessionRef.current = false; // allow showing once per login
      fetchData();
    }
  }, [user, fetchData]);

  const acceptDocuments = useCallback(
    async (docIds: string[]) => {
      if (!user || docIds.length === 0) return;
      let ipAddress: string | null = null;
      try {
        const resp = await fetch('https://api.ipify.org?format=json');
        const json = await resp.json();
        ipAddress = json.ip;
      } catch (err) {
        console.warn('[LegalProvider] Unable to fetch public IP address:', err);
      }
      const rows = docIds.map((id) => {
        const base = { user_id: user.id, document_id: id } as any;
        if (ipAddress) {
          base.ip_address = ipAddress;
        }
        return base;
      });
      const { error } = await supabase.from('document_acceptances').insert(rows);
      if (error) throw error;
      await fetchData();
    },
    [user, fetchData]
  );

  const value: LegalContextValue = {
    loading,
    pendingDocuments,
    acceptedDocuments,
    refresh: fetchData,
    acceptDocuments,
    showModal,
    dismissModal: () => setShowModal(false),
  };

  return (
    <LegalContext.Provider value={value}>
      {children}
      <LegalAcceptanceModal />
    </LegalContext.Provider>
  );
}

export function useLegal() {
  const ctx = useContext(LegalContext);
  if (ctx === undefined) {
    throw new Error('useLegal must be used within a LegalProvider');
  }
  return ctx;
} 